import { UnknownAction } from '@reduxjs/toolkit'
import { OnboardingStep } from '@repo/shared/dtos/organization'
import { isEmptyString } from '@repo/shared/utils'
import { generateId } from '@repo/shared/uuid/uuid'
import { DataStatus } from '@repo/ui/utils'
import debounce from 'lodash.debounce'

import { AppDispatch } from '@/main'
import { RootState } from '@/store'
import { adminStoreActions, storeActions } from '@/store/actions'
import { getFilterTemplates } from '@/store/beneficiary-list-filters/thunks'
import { getCurrentOrganization } from '@/store/helpers'
import { Action, MiddlewareType } from '@/store/middlewares/types'
import { GenerateMoreTasksPayload, GenerationPayload } from '@/store/thunks/assistant'
import {
  GenerateDescriptionPayload,
  GenerateMethodologyPayload,
  GenerationArg,
} from '@/store/thunks/organization'

const DEBOUNCE_DELAY = 500
const DEBOUNCE_OPTIONS = { leading: false, trailing: true }

const debounceRefreshProfile = debounce(
  ({ dispatch, state }: { dispatch: AppDispatch; state: RootState }): void => {
    const beneficiaryId = state.beneficiary.currentBeneficiaryId
    if (!beneficiaryId) return
    void dispatch(
      storeActions.beneficiary.refreshProfile({
        beneficiaryId,
      }) as unknown as UnknownAction,
    )
  },
  DEBOUNCE_DELAY,
  DEBOUNCE_OPTIONS,
)

const debounceRefreshStructuredData = debounce(
  ({ dispatch, state }: { dispatch: AppDispatch; state: RootState }): void => {
    const beneficiaryId = state.beneficiary.currentBeneficiaryId
    if (!beneficiaryId) return
    void dispatch(
      storeActions.beneficiary.refreshStructuredData({
        beneficiaryId,
      }) as unknown as UnknownAction,
    )
  },
  DEBOUNCE_DELAY,
  DEBOUNCE_OPTIONS,
)

export const effectsMiddleware =
  (): MiddlewareType =>
  // @ts-expect-error not easy to type correctly
  (store) => {
    return (next) => (action: Action) => {
      const newAction = next(action) // Required to be called at very first place

      const state = store.getState()

      const isAuthenticated = DataStatus.isFulfilled(state.user.me)

      if (action.type === storeActions.login.fulfilled.type) {
        // If the user just logged in, we need to initialize the app
        store.dispatch(storeActions.initApp() as unknown as UnknownAction)
      }

      if (!isAuthenticated) return newAction

      const organization = getCurrentOrganization(state)

      switch (action.type) {
        case storeActions.initOrganization.fulfilled.type:
          {
            if (!DataStatus.isFulfilled(organization)) return

            store.dispatch(getFilterTemplates() as unknown as UnknownAction)

            if (organization.onboarding.step === OnboardingStep.COMPLETED) return
            store.dispatch(
              adminStoreActions.onboarding.init.start({
                generationId: generateId(),
              }),
            )
          }
          break

        case adminStoreActions.onboarding.setNextStep.fulfilled.type:
          store.dispatch(adminStoreActions.onboarding.init.start({ generationId: generateId() }))
          break

        case adminStoreActions.onboarding.init.start.type:
          {
            if (!DataStatus.isFulfilled(organization)) return

            const { generationId } = action.payload as GenerationPayload
            if (generationId !== state.assistant.generationId) return

            switch (organization.onboarding.step) {
              case OnboardingStep.ORGANIZATION:
                {
                  if (!generationId) return
                  // No description yet
                  if (isEmptyString(organization.description)) {
                    store.dispatch(
                      adminStoreActions.organization.generateDescription({
                        generationId,
                      }) as unknown as UnknownAction,
                    )
                  }
                }
                break

              case OnboardingStep.METHODOLOGY:
                {
                  if (!generationId) return
                  // No methodology yet
                  if (isEmptyString(organization.methodology)) {
                    store.dispatch(
                      adminStoreActions.organization.generateMethodology({
                        generationId,
                      }) as unknown as UnknownAction,
                    )
                  }
                }
                break

              case OnboardingStep.SOURCES_PRIVATE:
              case OnboardingStep.MEMBERS:
                {
                  store.dispatch(adminStoreActions.initAdmin() as unknown as UnknownAction)
                }
                break
            }
          }
          break

        case adminStoreActions.organization.generateDescription.fulfilled.type:
          {
            if (!DataStatus.isFulfilled(organization)) return

            const { description, traceUrl } = action.payload as GenerateDescriptionPayload
            const { generationId } = action.meta.arg as GenerationArg
            if (!description || !generationId) return
            if (generationId !== state.assistant.generationId) return

            if (organization.onboarding.step === OnboardingStep.COMPLETED) {
              store.dispatch(
                storeActions.assistant.askFor.organization.updateDescription.start({
                  description,
                  generationId,
                  traceUrl,
                }),
              )
            } else {
              // While Onboarding
              store.dispatch(
                adminStoreActions.organization.update({
                  fields: { description },
                }) as unknown as UnknownAction,
              )
            }
          }
          break

        case adminStoreActions.organization.generateMethodology.fulfilled.type:
          {
            if (!DataStatus.isFulfilled(organization)) return

            const { methodology, traceUrl } = action.payload as GenerateMethodologyPayload
            const { generationId } = action.meta.arg as GenerationArg
            if (!methodology || !generationId) return
            if (generationId !== state.assistant.generationId) return

            if (organization.onboarding.step === OnboardingStep.COMPLETED) {
              store.dispatch(
                storeActions.assistant.askFor.organization.updateMethodology.start({
                  methodology,
                  generationId,
                  traceUrl,
                }),
              )
            } else {
              // While Onboarding
              store.dispatch(
                adminStoreActions.organization.update({
                  fields: { methodology },
                }) as unknown as UnknownAction,
              )
            }
          }
          break

        case storeActions.assistant.generate.beneficiary.moreTasks.type:
          {
            const { generationId, generatedTasks } = action.payload as GenerateMoreTasksPayload

            if (!generationId) return
            if (generationId !== state.assistant.generationId) return

            const organization = getCurrentOrganization(state)
            if (!DataStatus.isFulfilled(organization)) return

            store.dispatch(
              storeActions.assistant.generate.beneficiary.caseWorkerTasks.start({
                generationId,
                previouslyGeneratedTasks: generatedTasks,
              }) as unknown as UnknownAction,
            )
          }
          break

        case storeActions.initDashboard.type:
        case storeActions.beneficiary.create.fulfilled.type:
          {
            store.dispatch(
              storeActions.assistant.generate.dashboard.beneficiaryProfiles.start({
                generationId: generateId(),
              }),
            )
          }
          break

        case storeActions.view.beneficiary.profile.type:
        case storeActions.beneficiary.update.fulfilled.type:
        case storeActions.beneficiary.document.create.fulfilled.type:
        case storeActions.beneficiary.document.delete.fulfilled.type:
        case storeActions.beneficiary.document.restore.fulfilled.type:
        case storeActions.beneficiary.document.update.fulfilled.type:
          {
            debounceRefreshProfile({ dispatch: store.dispatch, state })
            const organization = getCurrentOrganization(state)
            if (!DataStatus.isFulfilled(organization)) return

            if (organization.structuredDataSchema.length > 0)
              debounceRefreshStructuredData({
                dispatch: store.dispatch,
                state,
              })
          }
          break

        case storeActions.task.archive.fulfilled.type:
        case storeActions.task.create.fulfilled.type:
        case storeActions.task.delete.fulfilled.type:
        case storeActions.task.restore.fulfilled.type:
        case storeActions.task.update.fulfilled.type:
        case storeActions.task.updateDueDate.fulfilled.type:
          {
            debounceRefreshProfile({ dispatch: store.dispatch, state })
          }
          break
      }
      return newAction
    }
  }
